import { addMonths } from 'date-fns/addMonths';
import { differenceInDays } from 'date-fns/differenceInDays';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import { subDays } from 'date-fns/subDays';
import { useEffect, useState } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';

import { useConfig } from '../defaultConfig';
import { useBankingHolidays } from '../fetchHooks/useBankingDays';
import { useAllLoanPayments } from '../fetchHooks/useLoans';
import useNextSalaryQuery from '../fetchHooks/useNextSalaryQuery';
import type { Account, LoanPayment, PaymentItem, SalaryInfoResponse } from '../generated-types';
import * as FuturePaymentService from '../services/core/FuturePaymentService';
import { addRunningBalance, parseDate } from '../utils/mapper/futurepaymentsUtil';

export type NextSalaryArgs = {
    salaryInfo: SalaryInfoResponse;
    salaryAccount: Account;
    futurePayments: PaymentItem[];
    bankHolidayDates: ZonedDate[];
};

const isBankHoliday = (date: ZonedDate, bankHolidayDates: ZonedDate[]) => {
    return bankHolidayDates.some((holiday) => isSameDay(holiday, date));
};

const adjustForBankingDays = (date: ZonedDate, bankHolidayDates: ZonedDate[]): ZonedDate => {
    if (!isBankHoliday(date, bankHolidayDates)) return date;
    return adjustForBankingDays(subDays(date, 1), bankHolidayDates);
};

const findNextValidPayday = (
    paydaysThisMonth: number[],
    paydays: number[],
    monthOffset: number,
    today: ZonedDate,
    bankHolidayDates: ZonedDate[],
): ZonedDate => {
    if (monthOffset > 2) {
        throw new Error('No valid payday found');
    }

    if (paydaysThisMonth.length === 0) {
        return findNextValidPayday(paydays, paydays, monthOffset + 1, today, bankHolidayDates);
    }

    const nextPayday = paydaysThisMonth[0];
    const payday = getZonedDate(new Date(today.getFullYear(), today.getMonth() + monthOffset, nextPayday));
    const adjustedPayday = adjustForBankingDays(payday, bankHolidayDates);

    if (isBefore(adjustedPayday, today)) {
        return findNextValidPayday(paydaysThisMonth.slice(1), paydays, monthOffset, today, bankHolidayDates);
    }
    return adjustedPayday;
};

const getNextPayday = (salaryInfo: SalaryInfoResponse, bankHolidayDates: ZonedDate[]): ZonedDate => {
    const today = getZonedDate();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

    const { paydays } = salaryInfo;
    if (salaryInfo.lastDayOfMonth) paydays.push(lastDayOfMonth);

    const paydaysAfterTodayThisMonth = paydays.filter((payday) => payday > today.getDate()).sort((a, b) => a - b);
    const allPaydays = paydays.sort((a, b) => a - b);
    return findNextValidPayday(paydaysAfterTodayThisMonth, allPaydays, 0, today, bankHolidayDates);
};

const getDaysLeft = (nextPayday: Date) => {
    if (!nextPayday) return -1;
    return differenceInDays(nextPayday, getZonedDate()) + 1;
};

const getAmountLeft = (futurePayments: PaymentItem[], salaryAccount: Account, nextPayday: Date) => {
    return (
        addRunningBalance(
            futurePayments
                .map(parseDate)
                .filter((payment) => {
                    const paymentDate = new Date(payment.plannedExecutionDate ?? payment.dueDate);
                    return isBefore(paymentDate, nextPayday);
                })
                .reverse(),
            salaryAccount.balance.availableBalance,
        ).slice(-1)[0]?.availableAfterPayment ?? salaryAccount.balance.availableBalance
    );
};

export type SalaryDetailsResponse = {
    nextPayday: ZonedDate;
    daysLeft: number;
    amountLeft: number;
};

export const getDetails = ({
    salaryInfo,
    salaryAccount,
    futurePayments,
    bankHolidayDates,
}: NextSalaryArgs): SalaryDetailsResponse => {
    const nextPayday = getNextPayday(salaryInfo, bankHolidayDates);
    const daysLeft = getDaysLeft(nextPayday);
    const amountLeft = getAmountLeft(futurePayments, salaryAccount, nextPayday);

    return { nextPayday, daysLeft, amountLeft };
};

export const getDetailsWithLoanPayments = (
    salaryData: [SalaryInfoResponse, Account, PaymentItem[]] | undefined,
    loanPayments: LoanPayment[] | undefined,
    bankHolidayDates: ZonedDate[],
): SalaryDetailsResponse | undefined => {
    if (!salaryData || !loanPayments) return undefined;
    const [salaryInfo, salaryAccount, futurePayments] = salaryData;
    return getDetails({
        futurePayments: FuturePaymentService.extendWithFakedLoans(
            futurePayments,
            loanPayments,
            salaryAccount.accountNumber,
        ),
        salaryAccount,
        salaryInfo,
        bankHolidayDates,
    });
};

const useNextSalary = () => {
    const [nextSalaryDetails, setNextSalaryDetails] = useState<SalaryDetailsResponse>();

    const nextSalaryQuery = useNextSalaryQuery();
    const {
        featureToggles: { enableFakedLoanFuturePayment },
    } = useConfig();

    const { data: loanPayments = [], error: loanPaymentError } = useAllLoanPayments(
        enableFakedLoanFuturePayment && nextSalaryQuery.status !== 'suspended',
    );

    const salaryAccount = nextSalaryQuery.data?.[1];
    const nextSalaryError = nextSalaryQuery.error ?? loanPaymentError;

    const { data: bankHolidayDates } = useBankingHolidays(getZonedDate(), addMonths(getZonedDate(), 2));

    useEffect(() => {
        if (bankHolidayDates) {
            setNextSalaryDetails(getDetailsWithLoanPayments(nextSalaryQuery.data, loanPayments, bankHolidayDates));
        }
    }, [nextSalaryQuery.data, loanPayments, bankHolidayDates]);

    return { nextSalaryDetails, salaryAccount, nextSalaryError, nextSalaryQueryStatus: nextSalaryQuery.status };
};

export default useNextSalary;
